// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/PaymentIn.tsx
//
// تغییرات این نسخه:
// - 🆕 پشتیبانی از دریافت پول هم از مشتری و هم از فروشنده
// - 🆕 انتخابگر "نوع طرف حساب": مشتری / فروشنده
// - 🆕 لیست فروشندگان از vendorsAPI کشیده می‌شود
// - 🆕 منطق سرور: vendor_id یا customer_id ارسال می‌شود
// 
// هیچ بخش دیگری دست نخورده است
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, customersAPI, vendorsAPI, accountsAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, Edit2, Printer } from "lucide-react";
import { printReceiptIn } from "../../../utils/InvoicePrint";
import JournalFilter, { emptyFilter, JournalFilterValues } from "./JournalFilter";
import AttachmentField from "../../../components/AttachmentField";
import { fileUrl } from "../../../utils/txnReport";
import { useTxnList, describeFilter } from "./useTxnList";
import TxnReportModal from "./TxnReportModal";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

const partyName = (p: any) =>
  `${p.first_name || ""} ${p.last_name || ""} ${p.company_name || ""}`.trim();

export default function PaymentIn() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [showReport, setShowReport] = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [editItem, setEditItem]   = useState<any>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [vendors, setVendors]     = useState<any[]>([]); // 🆕
  const [cashAccs, setCashAccs]   = useState<any[]>([]);
  const [filter, setFilter]       = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]         = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"confirmed">("all");

  const { list, loading, page, setPage, totalPages, totalAll, pendingCount, confirmedCount, reload } = useTxnList({
    fetcher: (p) => journalAPI.listPaymentsIn(p),
    respKey: "payments",
    filter, statusFilter,
  });

  // 🆕 party_type: 'customer' یا 'vendor'
  const [form, setForm] = useState({
    party_type: "customer" as "customer" | "vendor",
    date: todayGregorian(),
    customer_id: "", customer_currency: "USD",
    vendor_id: "",   vendor_currency: "USD",
    receipt_currency: "USD", receipt_amount: "",
    exchange_rate: "1", account_id: "", account_name: "",
    notes: "", attachment: "",
  });

  const loadMeta = useCallback(async () => {
    const [c, v, a] = await Promise.all([
      customersAPI.list(),
      vendorsAPI.list(), // 🆕
      accountsAPI.getCashAccounts(),
    ]);
    if (c.success) setCustomers(c.customers || []);
    if (v.success) setVendors(v.vendors || []); // 🆕
    if (a.success) setCashAccs(a.accounts || []);
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  const load = () => { loadMeta(); reload(); };

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const calcAmt = (amt: string, rate: string, cur: string) => {
    const a = parseFloat(amt || "0");
    const r = parseFloat(rate || "1");
    if (!a || !r) return 0;
    return cur === "USD" ? a / r : a * r;
  };

  // 🆕 ارز طرف حساب
  const partyCurrency = form.party_type === "customer" ? form.customer_currency : form.vendor_currency;
  const custAmount    = calcAmt(form.receipt_amount, form.exchange_rate, partyCurrency);

  const editPartyCurrency = editItem
    ? (editItem.party_type === "customer" ? editItem.customer_currency : editItem.vendor_currency)
    : "USD";
  const editCustAmount = editItem
    ? calcAmt(editItem.receipt_amount, editItem.exchange_rate, editPartyCurrency)
    : 0;

  const buildSubmitData = (statusValue: "pending"|"confirmed") => {
    const account  = cashAccs.find((a: any) => String(a.id) === form.account_id);
    const base: any = {
      date: form.date,
      receipt_currency: form.receipt_currency,
      receipt_amount: parseFloat(form.receipt_amount),
      exchange_rate: parseFloat(form.exchange_rate || "1"),
      customer_amount: custAmount,
      account_id: parseInt(form.account_id),
      account_name: account?.name || form.account_name,
      notes: form.notes, attachment: form.attachment,
      status: statusValue,
      party_type: form.party_type, // 🆕
    };
    if (form.party_type === "customer") {
      const customer = customers.find((c: any) => String(c.id) === form.customer_id);
      base.customer_id = parseInt(form.customer_id);
      base.customer_name = partyName(customer || {});
      base.customer_currency = customer?.currency || form.customer_currency;
      base.vendor_id = null;
    } else {
      const vendor = vendors.find((v: any) => String(v.id) === form.vendor_id);
      base.vendor_id = parseInt(form.vendor_id);
      base.customer_name = partyName(vendor || {}); // برای نمایش از همان customer_name استفاده می‌کنیم
      base.customer_currency = vendor?.currency || form.vendor_currency;
      base.customer_id = null;
    }
    return base;
  };

  const validate = () => {
    if (form.party_type === "customer" && !form.customer_id) { showToast("مشتری الزامی است", false); return false; }
    if (form.party_type === "vendor" && !form.vendor_id)     { showToast("فروشنده الزامی است", false); return false; }
    if (!form.receipt_amount) { showToast("مبلغ الزامی است", false); return false; }
    if (!form.account_id)     { showToast("حساب دریافتی الزامی است", false); return false; }
    return true;
  };

  const resetForm = () => setForm({
    party_type: "customer",
    date: todayGregorian(),
    customer_id: "", customer_currency: "USD",
    vendor_id: "", vendor_currency: "USD",
    receipt_currency: "USD", receipt_amount: "",
    exchange_rate: "1", account_id: "", account_name: "", notes: "", attachment: ""
  });

  const handleSubmitConfirmed = async () => {
    if (!validate()) return;
    const res = await journalAPI.addPaymentIn(buildSubmitData("confirmed"));
    if (res.success) {
      showToast(`دریافت ${res.serial} با موفقیت ثبت شد`);
      setShowForm(false);
      resetForm();
      load();
    } else showToast(res.message || "خطا", false);
  };

  const handleSubmitPending = async () => {
    if (!validate()) return;
    const res = await journalAPI.addPaymentIn(buildSubmitData("pending"));
    if (res.success) {
      showToast(`دریافت ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false);
      resetForm();
      load();
    } else showToast(res.message || "خطا", false);
  };

  const openEdit = (p: any) => {
    // 🆕 تشخیص party_type از روی vendor_id یا customer_id
    const isVendor = p.vendor_id && !p.customer_id;
    setEditItem({
      id: p.id, date: p.date,
      party_type: isVendor ? "vendor" : "customer",
      customer_id: String(p.customer_id || ""),
      vendor_id: String(p.vendor_id || ""),
      customer_currency: p.customer_currency || "USD",
      vendor_currency: p.customer_currency || "USD", // در DB در همان فیلد ذخیره می‌شود
      receipt_currency: p.receipt_currency, receipt_amount: String(p.receipt_amount),
      exchange_rate: String(p.exchange_rate || "1"),
      account_id: String(p.account_id), account_name: p.account_name,
      notes: p.notes || "", attachment: p.attachment || "",
      status: p.status || "confirmed",
    });
  };

  const handleEdit = async () => {
    if (editItem.party_type === "customer" && !editItem.customer_id) { showToast("مشتری الزامی است", false); return; }
    if (editItem.party_type === "vendor" && !editItem.vendor_id)     { showToast("فروشنده الزامی است", false); return; }
    if (!editItem.receipt_amount) { showToast("مبلغ الزامی است", false); return; }
    if (!editItem.account_id)     { showToast("حساب الزامی است", false); return; }
    const account  = cashAccs.find((a: any) => String(a.id) === editItem.account_id);
    const payload: any = {
      id: editItem.id, date: editItem.date,
      receipt_currency: editItem.receipt_currency,
      receipt_amount: parseFloat(editItem.receipt_amount),
      exchange_rate: parseFloat(editItem.exchange_rate || "1"),
      customer_amount: editCustAmount,
      account_id: parseInt(editItem.account_id),
      account_name: account?.name || editItem.account_name,
      notes: editItem.notes, attachment: editItem.attachment,
      party_type: editItem.party_type, // 🆕
    };
    if (editItem.party_type === "customer") {
      const customer = customers.find((c: any) => String(c.id) === editItem.customer_id);
      payload.customer_id = parseInt(editItem.customer_id);
      payload.customer_name = partyName(customer || {});
      payload.customer_currency = customer?.currency || editItem.customer_currency;
      payload.vendor_id = null;
    } else {
      const vendor = vendors.find((v: any) => String(v.id) === editItem.vendor_id);
      payload.vendor_id = parseInt(editItem.vendor_id);
      payload.customer_name = partyName(vendor || {});
      payload.customer_currency = vendor?.currency || editItem.vendor_currency;
      payload.customer_id = null;
    }
    const res = await journalAPI.updatePaymentIn(payload);
    if (res.success) { showToast("دریافت ویرایش شد"); setEditItem(null); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف دریافت ${serial}؟`)) return;
    const res = await journalAPI.deletePaymentIn(id);
    if (res.success) { showToast("دریافت حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی دریافت ${serial}؟\nموجودی حساب و بیلانس طرف حساب به‌روزرسانی می‌شود.`)) return;
    const res = await journalAPI.confirmPaymentIn(id);
    if (res.success) { showToast("دریافت تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن دریافت ${serial} به حالت در انتظار؟\nتأثیر مالی آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertPaymentIn(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };


  const filtersLabel = describeFilter(filter, { counterpartyLabel: "طرف حساب", counterparty: [...customers.map((c: any) => ({ value: `c-${c.id}`, label: partyName(c) })), ...vendors.map((v: any) => ({ value: `v-${v.id}`, label: partyName(v) }))], account: cashAccs.map((a: any) => ({ value: String(a.id), label: a.name })) });

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در دریافت‌ها... (شماره سند، نام طرف حساب، توضیحات)",
          showDateRange: true,
          showAmountRange: true,
          showCurrency: true,
          showCounterparty: true,
          counterpartyLabel: "طرف حساب",
          counterpartyOptions: [
            ...customers.map((c: any) => ({
              value: `c-${c.id}`,
              label: `🤝 ${partyName(c)}`,
            })),
            ...vendors.map((v: any) => ({
              value: `v-${v.id}`,
              label: `🏪 ${partyName(v)}`,
            })),
          ],
          showAccount: true,
          accountOptions: cashAccs.map((a: any) => ({ value: String(a.id), label: a.name })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={list.length}
        totalCount={totalAll}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        T={T}
        dark={dark}
        onReport={() => setShowReport(true)}
        reportLabel="گزارش PDF / Excel دریافت پول"
      />

      {/* فیلتر وضعیت + دکمه ثبت */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={pendingCount + confirmedCount}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
        />
        {canAdd && <button onClick={() => setShowForm(true)} style={addBtnSt("#3b82f6")}><Plus size={15} /> ثبت دریافت پول</button>}
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "68vh", overflowY: "auto" }}>
          {list.length === 0 && <Empty T={T} text="هیچ دریافتی ثبت نشده" />}
          {/* ردیف هدر */}
          {list.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px 1fr 110px 1fr 1.2fr 200px 200px",
              gap: 12,
              padding: "10px 16px",
              borderRadius: 10,
              background: dark ? "#1e293b" : "#f1f5f9",
              position: "sticky" as const, top: 0, zIndex: 2,
              border: `1px solid ${T.cardBorder}`,
              fontSize: 11,
              fontWeight: 700,
              color: T.subtext,
            }}>
              <div>شماره سند</div>
              <div>طرف حساب</div>
              <div>تاریخ</div>
              <div>حساب</div>
              <div>شرح</div>
              <div style={{ textAlign: "left" as const }}>مبلغ دریافتی</div>
              <div style={{ textAlign: "center" as const }}>عملیات</div>
            </div>
          )}
          {list.map((p: any) => {
            const isPending = (p.status || "confirmed") === "pending";
            const isVendor  = p.vendor_id && !p.customer_id; // 🆕
            return (
            <div key={p.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {}),
              display: "grid",
              gridTemplateColumns: "130px 1fr 110px 1fr 1.2fr 200px 200px",
              gap: 12,
              alignItems: "center",
            }}>
              {/* ستون 1: سریال + بدج */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                <span style={badgeSt("#3b82f6")}>{p.serial}</span>
                {isPending && <PendingBadge />}
                {p.attachment && <a href={fileUrl(p.attachment)} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "#3b82f6", textDecoration: "none" }}>📎 ضمیمه</a>}
              </div>

              {/* ستون 2: نام + نوع 🆕 */}
              <div style={{ overflow: "hidden" }}>
                <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
                  {isVendor ? "🏪 " : "🤝 "}{p.customer_name}
                </div>
                <span style={{
                  fontSize: 9,
                  background: isVendor ? "rgba(245,158,11,0.15)" : "rgba(139,92,246,0.15)",
                  color: isVendor ? "#f59e0b" : "#8b5cf6",
                  padding: "1px 6px",
                  borderRadius: 4,
                  fontWeight: 700,
                  marginTop: 2,
                  display: "inline-block",
                }}>
                  {isVendor ? "فروشنده" : "مشتری"}
                </span>
              </div>

              {/* ستون 3: تاریخ */}
              <div>
                <DualDate date={p.date} fontSize={12} color={T.subtext} />
              </div>

              {/* ستون 4: حساب */}
              <div style={{ fontSize: 12, color: T.subtext, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
                {p.account_name}
              </div>

              {/* ستون 5: شرح */}
              <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" as const }}>
                {p.notes || "—"}
              </div>

              {/* ستون 6: مبلغ */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                <span style={{ fontWeight: 800, color: "#3b82f6", fontSize: 14, direction: "ltr" as const }}>+{fmt(p.receipt_amount)} {p.receipt_currency}</span>
                {p.receipt_currency !== p.customer_currency && <span style={{ fontSize: 11, color: T.subtext, direction: "ltr" as const }}>≈ {fmt(p.customer_amount)} {p.customer_currency}</span>}
              </div>

              {/* ستون 7: عملیات */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                {isPending && canEdit && (
                  <ConfirmIconButton onClick={() => handleConfirm(p.id, p.serial)} />
                )}
                {!isPending && canEdit && (
                  <RevertIconButton onClick={() => handleRevert(p.id, p.serial)} />
                )}
                <button onClick={() => {
                  const party = isVendor
                    ? vendors.find((v: any) => v.id === p.vendor_id)
                    : customers.find((c: any) => c.id === p.customer_id);
                  printReceiptIn(p, party, party?.current_balance || party?.balance || 0);
                }} title="چاپ رسید" style={iconBtnSt("#0ea5e9")}><Printer size={13} /></button>
                {canEdit && <button onClick={() => openEdit(p)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                {canDelete && <button onClick={() => handleDelete(p.id, p.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت */}
      {showForm && (
        <Modal title="ثبت دریافت پول" onClose={() => setShowForm(false)} dark={dark} T={T}>
          {/* 🆕 انتخابگر نوع طرف حساب */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, padding: 6, background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", borderRadius: 10, border: `1px solid ${T.cardBorder}` }}>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, party_type: "customer", vendor_id: "" }))}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                background: form.party_type === "customer" ? "linear-gradient(135deg,#8b5cf6,#7c3aed)" : "transparent",
                color: form.party_type === "customer" ? "#fff" : T.text,
                transition: "all 0.2s",
              }}>
              🤝 دریافت از مشتری
            </button>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, party_type: "vendor", customer_id: "" }))}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                background: form.party_type === "vendor" ? "linear-gradient(135deg,#f59e0b,#d97706)" : "transparent",
                color: form.party_type === "vendor" ? "#fff" : T.text,
                transition: "all 0.2s",
              }}>
              🏪 دریافت از فروشنده
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {form.party_type === "customer" ? (
              <div>
                <Lbl T={T}>مشتری *</Lbl>
                <SearchableSelect options={customers.map((c: any) => ({ value: String(c.id), label: `${partyName(c)} (${c.currency})` }))} value={form.customer_id}
                  onChange={v => { const cu = customers.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, customer_id: v, customer_currency: cu?.currency || "USD" })); }} placeholder="انتخاب مشتری" />
              </div>
            ) : (
              <div>
                <Lbl T={T}>فروشنده *</Lbl>
                <SearchableSelect options={vendors.map((v: any) => ({ value: String(v.id), label: `${partyName(v)} (${v.currency})` }))} value={form.vendor_id}
                  onChange={v => { const ve = vendors.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, vendor_id: v, vendor_currency: ve?.currency || "USD" })); }} placeholder="انتخاب فروشنده" />
              </div>
            )}
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>ارز دریافتی</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={form.receipt_currency} onChange={v => setForm(f => ({ ...f, receipt_currency: v }))} placeholder="انتخاب ارز" /></div>
            <div><Lbl T={T}>مبلغ دریافتی *</Lbl><input type="number" value={form.receipt_amount} onChange={e => setForm(f => ({ ...f, receipt_amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله ({partyCurrency})</Lbl><input type="number" value={form.exchange_rate} onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به ارز طرف حساب ({partyCurrency})</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(59,130,246,0.1)" : "#eff6ff", color: "#3b82f6", fontWeight: 700, fontSize: 14, border: "1.5px solid #3b82f620" }}>{fmt(custAmount)} {partyCurrency}</div>
              <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>{partyCurrency === "USD" ? `${form.receipt_amount || 0} ÷ ${form.exchange_rate || 1}` : `${form.receipt_amount || 0} × ${form.exchange_rate || 1}`}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>حساب دریافتی *</Lbl>
              <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={form.account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="فقط حساب‌های دارایی نقدی" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه (فایل یا صدا)</Lbl><AttachmentField value={form.attachment || ""} fileName={form.attachment ? String(form.attachment).split("/").pop() : ""} onChange={(path) => setForm((f: any) => ({ ...f, attachment: path }))} uploadFn={(file) => journalAPI.uploadAttachment(file)} T={T} dark={dark} /></div>
          </div>
          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره دریافت در انتظار"
              />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmitConfirmed)}
                loading={submitting}
                color="#3b82f6"
                label="ثبت دریافت"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش */}
      {editItem && (
        <Modal title={`ویرایش دریافت پول${editItem.status==="pending" ? " (در انتظار)" : ""}`} onClose={() => setEditItem(null)} dark={dark} T={T}>
          {/* 🆕 انتخابگر نوع طرف حساب در ویرایش */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, padding: 6, background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", borderRadius: 10, border: `1px solid ${T.cardBorder}` }}>
            <button
              type="button"
              onClick={() => setEditItem((f: any) => ({ ...f, party_type: "customer", vendor_id: "" }))}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                background: editItem.party_type === "customer" ? "linear-gradient(135deg,#8b5cf6,#7c3aed)" : "transparent",
                color: editItem.party_type === "customer" ? "#fff" : T.text,
              }}>
              🤝 از مشتری
            </button>
            <button
              type="button"
              onClick={() => setEditItem((f: any) => ({ ...f, party_type: "vendor", customer_id: "" }))}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                background: editItem.party_type === "vendor" ? "linear-gradient(135deg,#f59e0b,#d97706)" : "transparent",
                color: editItem.party_type === "vendor" ? "#fff" : T.text,
              }}>
              🏪 از فروشنده
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {editItem.party_type === "customer" ? (
              <div>
                <Lbl T={T}>مشتری *</Lbl>
                <SearchableSelect options={customers.map((c: any) => ({ value: String(c.id), label: `${partyName(c)} (${c.currency})` }))} value={editItem.customer_id}
                  onChange={v => { const cu = customers.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, customer_id: v, customer_currency: cu?.currency || "USD" })); }} placeholder="انتخاب مشتری" />
              </div>
            ) : (
              <div>
                <Lbl T={T}>فروشنده *</Lbl>
                <SearchableSelect options={vendors.map((v: any) => ({ value: String(v.id), label: `${partyName(v)} (${v.currency})` }))} value={editItem.vendor_id}
                  onChange={v => { const ve = vendors.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, vendor_id: v, vendor_currency: ve?.currency || "USD" })); }} placeholder="انتخاب فروشنده" />
              </div>
            )}
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>ارز دریافتی</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={editItem.receipt_currency} onChange={v => setEditItem((f: any) => ({ ...f, receipt_currency: v }))} placeholder="انتخاب ارز" /></div>
            <div><Lbl T={T}>مبلغ دریافتی *</Lbl><input type="number" value={editItem.receipt_amount} onChange={e => setEditItem((f: any) => ({ ...f, receipt_amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله ({editPartyCurrency})</Lbl><input type="number" value={editItem.exchange_rate} onChange={e => setEditItem((f: any) => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به ارز طرف حساب ({editPartyCurrency})</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(59,130,246,0.1)" : "#eff6ff", color: "#3b82f6", fontWeight: 700, fontSize: 14, border: "1.5px solid #3b82f620" }}>{fmt(editCustAmount)} {editPartyCurrency}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>حساب دریافتی *</Lbl>
              <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={editItem.account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="انتخاب حساب" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه (فایل یا صدا)</Lbl><AttachmentField value={editItem.attachment || ""} fileName={editItem.attachment ? String(editItem.attachment).split("/").pop() : ""} onChange={(path) => setEditItem((f: any) => ({ ...f, attachment: path }))} uploadFn={(file) => journalAPI.uploadAttachment(file)} T={T} dark={dark} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#3b82f6" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}
      {showReport && (
        <TxnReportModal type="payments_in" title="دریافت پول" filter={filter} filtersLabel={filtersLabel} onClose={() => setShowReport(false)} T={T} dark={dark} />
      )}
    </div>
  );
}